import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { listScenarioBgmHistory } from '../../../features/scenarios/api';
import { fetchMaterialSignedUrl, requestDeleteTemporary } from '../../../features/materials/api';
import { formatDateTimeShort as formatDate } from '../../../utils/formatters';
import { bgmProviderLabel } from './constants';
import { useLockScrollOutside } from '../../../hooks/useLockScrollOutside';
import styles from './ScenarioGenerator.module.css';
import { resolveBgmDisplayName } from './bgmDisplayName';

interface BgmHistoryItem {
  id: string;
  created_at: string;
  is_current: boolean;
  filename?: string;
  /** テンプレート生成 (本文非開示) は null */
  prompt?: string | null;
  song_title?: string;
  /** テンプレート生成のテンプレート名 (表示名は song_title に同じ値が入る) */
  template_label?: string | null;
  /** 生成プロバイダ (elevenlabs / lyria / lyria-pro)。アップロード素材は無い */
  provider?: string | null;
  model?: string | null;
  name?: string | null;
  file_size_bytes?: number;
  duration_seconds?: number;
  preview_url?: string;
  source_url?: string;
  storage_bucket?: string;
  storage_path?: string;
  resolvedUrl?: string;
}

/** BGM履歴セレクタ（ナレーション履歴と同じスタイル） */
export function BgmHistorySelector({
  scenarioId,
  currentMaterialId,
  onSelect,
  onDelete,
  selectedSongTitle,
  durationSeconds,
  excluded,
  onToggleExcluded,
}: {
  scenarioId: string;
  currentMaterialId?: string;
  onSelect: (materialId: string) => void;
  onDelete?: () => void;
  selectedSongTitle?: string;
  durationSeconds?: number | null;
  /** 「採用しない」トグル (プレビュー・エクスポートから除外)。ポップアップ最上部に行を出す */
  excluded?: boolean;
  onToggleExcluded?: (excluded: boolean) => void;
}) {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [dropdownPos, setDropdownPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const [items, setItems] = useState<BgmHistoryItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [playingId, setPlayingId] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setPlayingId(null);
  };

  const togglePlay = async (item: BgmHistoryItem) => {
    if (playingId === item.id) {
      stopAudio();
      return;
    }
    stopAudio();

    let url = item.resolvedUrl;
    if (!url) {
      try {
        url = await fetchMaterialSignedUrl(item.id, true) || undefined;
      } catch {
        // fallback
      }
      if (url) {
        setItems(prev => prev.map(i => i.id === item.id ? { ...i, resolvedUrl: url } : i));
      }
    }
    if (!url) return;

    const audio = new Audio(url);
    audioRef.current = audio;
    setPlayingId(item.id);
    audio.play();
    audio.onended = () => stopAudio();
    audio.onerror = () => stopAudio();
  };

  // 外側クリックで閉じる
  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        dropdownRef.current && !dropdownRef.current.contains(target) &&
        buttonRef.current && !buttonRef.current.contains(target)
      ) {
        stopAudio();
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  // ポップアップは開いた位置に fixed で置くため、開いている間は背景を動かさない
  useLockScrollOutside(open, dropdownRef);

  const updateDropdownPos = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPos({ top: rect.top, left: rect.left });
    }
  };

  const fetchHistory = async () => {
    if (open) {
      setOpen(false);
      return;
    }
    setLoading(true);
    try {
      setItems((await listScenarioBgmHistory(scenarioId)) as BgmHistoryItem[]);
      updateDropdownPos();
      setOpen(true);
    } catch {
      // 取得失敗
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (materialId: string) => {
    if (playingId === materialId) stopAudio();
    const isCurrent = materialId === currentMaterialId;
    try {
      await requestDeleteTemporary(materialId);
      setItems(prev => prev.filter(i => i.id !== materialId));
      if (isCurrent && onDelete) {
        onDelete();
        setOpen(false);
      }
    } catch {
      // 削除失敗
    }
  };

  const formatSize = (bytes?: number) => {
    if (!bytes) return null;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)}KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
  };

  const formatDuration = (sec?: number | null) => {
    if (!sec) return null;
    return `${Math.round(sec)}秒`;
  };

  const truncatedTitle = selectedSongTitle
    ? (selectedSongTitle.length > 30 ? selectedSongTitle.slice(0, 30) + '…' : selectedSongTitle)
    : undefined;
  const durationLabel = formatDuration(durationSeconds);
  const displayTitle = truncatedTitle
    ? (durationLabel ? `${truncatedTitle} (${durationLabel})` : truncatedTitle)
    : (durationLabel ? `BGM (${durationLabel})` : undefined);

  const dropdownContent = (list: BgmHistoryItem[]) => (
    <div
      ref={dropdownRef}
      className={`modal-surface ${styles.narrationHistoryDropdown}`}
      style={{
        position: 'fixed',
        bottom: `${window.innerHeight - dropdownPos.top + 4}px`,
        left: `${dropdownPos.left}px`,
      }}
    >
      {/* 最上部: 「採用しない」行 (右寄せ・非活性色)。BGM素材は保持したままプレビュー
          MP4・エクスポートから外す。採用に戻すUIは無く、いずれかのテイクを選ぶと戻る。 */}
      {onToggleExcluded && currentMaterialId && !excluded && (
        <div
          className={styles.narrationHistoryItem}
          style={{ borderBottom: 'var(--modal-divider, 1px solid rgba(255, 255, 255, 0.08))' }}
        >
          <button
            type="button"
            className={styles.narrationHistorySelect}
            style={{ justifyContent: 'flex-end', color: 'rgba(245, 241, 230, 0.45)' }}
            onClick={() => {
              onToggleExcluded(true);
              stopAudio();
              setOpen(false);
            }}
            title="プレビュー動画やエクスポートに含めません。BGMを選ぶと採用に戻ります"
          >
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12" /></svg>
              採用しない
            </span>
          </button>
        </div>
      )}
      {list.length > 0 ? list.map((item) => (
        <div
          key={item.id}
          className={`${styles.narrationHistoryItem} ${item.id === currentMaterialId ? styles.narrationHistoryCurrent : ''}`}
        >
          <button
            type="button"
            className={styles.narrationHistorySelect}
            onClick={() => {
              onSelect(item.id);
              stopAudio();
              setOpen(false);
            }}
          >
            <span className={styles.narrationHistoryInfo}>
              <span>{formatDate(item.created_at)}</span>
              {(() => {
                // 表示名はヘッダー / トリガーと同じ規則 (bgmDisplayName.ts: name > song_title >
                // アップロード元ファイル名 > 「BGM (自動生成)」)。storage_path 由来の名前は
                // タイムスタンプ付きで読めないため一覧では出さない。
                const label = resolveBgmDisplayName({
                  name: item.name,
                  source_type: 'bgm',
                  generation_metadata: { song_title: item.song_title, filename: item.filename, prompt: item.prompt },
                });
                if (!label) return null;
                return (
                  <span className={styles.narrationHistoryVoice}>{label.length > 30 ? label.slice(0, 30) + '…' : label}</span>
                );
              })()}
              {item.duration_seconds && (
                // ナレーション履歴と同じ体系: 「n秒（モデルファミリー名）」
                <span className={styles.narrationHistoryDuration}>
                  {formatDuration(item.duration_seconds)}
                  {bgmProviderLabel(item.provider) ? `（${bgmProviderLabel(item.provider)}）` : ''}
                </span>
              )}
              {!item.duration_seconds && formatSize(item.file_size_bytes) && (
                <span className={styles.narrationHistoryDuration}>{formatSize(item.file_size_bytes)}</span>
              )}
            </span>
            {item.id === currentMaterialId && <span className={styles.narrationHistoryBadge}>選択中</span>}
          </button>
          <button
            type="button"
            className={styles.narrationHistoryPlay}
            onClick={(e) => {
              e.stopPropagation();
              togglePlay(item);
            }}
            title={playingId === item.id ? '停止' : '再生'}
          >
            {playingId === item.id ? (
              <svg width="16" height="16" viewBox="0 0 16 16">
                <circle cx="8" cy="8" r="7" fill="none" stroke="currentColor" strokeWidth="1.2" />
                <rect x="5.5" y="5.5" width="5" height="5" rx="0.5" fill="currentColor" />
              </svg>
            ) : (
              <svg width="16" height="16" viewBox="0 0 16 16">
                <circle cx="8" cy="8" r="7" fill="none" stroke="currentColor" strokeWidth="1.2" />
                <polygon points="6.5,4.5 12,8 6.5,11.5" fill="currentColor" />
              </svg>
            )}
          </button>
          <button
            type="button"
            className={styles.narrationHistoryDelete}
            onClick={(e) => {
              e.stopPropagation();
              handleDelete(item.id);
            }}
            title="削除"
          >
            <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
              <path d="M5.5 1.5h5M2.5 4h11M6 4v8.5M10 4v8.5M3.5 4l.5 10.5a1 1 0 001 .5h6a1 1 0 001-.5L12.5 4" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>
      )) : (
        <div className={styles.narrationHistoryItem} style={{ cursor: 'default', opacity: 0.6 }}>
          履歴がありません
        </div>
      )}
    </div>
  );

  return (
    <div style={{ display: 'inline-block' }}>
      <button
        ref={buttonRef}
        type="button"
        className={styles.narrationHistoryButton}
        onClick={fetchHistory}
        disabled={loading}
        title="過去のBGM履歴"
      >
        {loading ? (
          <span className={styles.spinnerSmall} />
        ) : (
          <>
            {excluded ? (
              <span className={styles.narrationHistoryDurationLabel}>採用なし</span>
            ) : (
              displayTitle && <span className={styles.narrationHistoryVoiceLabel}>{displayTitle}</span>
            )}
            <span className={styles.narrationHistoryCaret}>&#9662;</span>
          </>
        )}
      </button>
      {open && createPortal(dropdownContent(items), document.body)}
    </div>
  );
}
